import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, BackHandler, Platform } from 'react-native';
import {
  Text,
  Button,
  Card,
  Dialog,
  Portal,
  useTheme,
  ActivityIndicator,
} from 'react-native-paper';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  updateSkillAssessment,
  selectAIProfileLoading,
} from '../../store/slices/aiProfileSlice';
import { AssessmentQuestion } from './AssessmentQuestion';
import { AssessmentProgress } from './AssessmentProgress';
import { assessmentQuestions } from './assessmentData';
import type {
  AssessmentResponse,
  AssessmentResults,
  AssessmentProgress as AssessmentProgressType,
} from './types';
import type { SkillLevel } from '@coffeeplexity/shared';
import { logger } from '../../utils/logger';

interface AISkillAssessmentProps {
  onComplete: (results: AssessmentResults) => void;
  onProgress?: (step: number, total: number) => void;
  allowSkip?: boolean;
  showProgress?: boolean;
  style?: any;
}

export function AISkillAssessment({
  onComplete,
  onProgress,
  allowSkip = true,
  showProgress = true,
  style,
}: AISkillAssessmentProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectAIProfileLoading);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [startTime] = useState(Date.now());
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const currentQuestion = assessmentQuestions[currentQuestionIndex];
  const isLastQuestion =
    currentQuestionIndex === assessmentQuestions.length - 1;

  // Calculate progress
  const progress: AssessmentProgressType = {
    currentStep: currentQuestionIndex + 1,
    totalSteps: assessmentQuestions.length,
    completedQuestions: responses.length,
    skippedQuestions: responses.filter(r => r.response === null).length,
    averageConfidence:
      responses.length > 0
        ? responses.reduce((sum, r) => sum + r.confidence, 0) / responses.length
        : 0,
    estimatedTimeRemaining: Math.max(
      0,
      (assessmentQuestions.length - currentQuestionIndex - 1) * 1.5
    ),
    categoryProgress: calculateCategoryProgress(),
  };

  function calculateCategoryProgress(): Record<string, number> {
    const categoryProgress: Record<string, number> = {};
    const categoryQuestions: Record<string, number> = {};
    const categoryCompleted: Record<string, number> = {};

    // Count total questions per category
    assessmentQuestions.forEach(q => {
      categoryQuestions[q.category] = (categoryQuestions[q.category] || 0) + 1;
    });

    // Count completed questions per category
    responses.forEach(response => {
      const question = assessmentQuestions.find(
        q => q.id === response.questionId
      );
      if (question) {
        categoryCompleted[question.category] =
          (categoryCompleted[question.category] || 0) + 1;
      }
    });

    // Calculate progress percentages
    Object.keys(categoryQuestions).forEach(category => {
      categoryProgress[category] =
        (categoryCompleted[category] || 0) / categoryQuestions[category];
    });

    return categoryProgress;
  }

  // Handle back button - only on native platforms
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        () => {
          if (currentQuestionIndex > 0) {
            setShowExitDialog(true);
            return true;
          }
          return false;
        }
      );

      return () => backHandler.remove();
    }
  }, [currentQuestionIndex]);

  // Report progress to parent
  useEffect(() => {
    onProgress?.(progress.currentStep, progress.totalSteps);
  }, [progress.currentStep, progress.totalSteps, onProgress]);

  const handleResponse = useCallback(
    async (response: AssessmentResponse) => {
      const newResponses = [...responses, response];
      setResponses(newResponses);

      if (isLastQuestion) {
        await processAssessmentResults(newResponses);
      } else {
        // Move to next question with adaptive logic
        const nextIndex = determineNextQuestion(newResponses);
        setCurrentQuestionIndex(nextIndex);
      }
    },
    [responses, isLastQuestion]
  );

  const handleSkip = useCallback(() => {
    // Skip is handled in handleResponse with null response
  }, []);

  const determineNextQuestion = (
    currentResponses: AssessmentResponse[]
  ): number => {
    // Simple linear progression for now
    // In a more advanced implementation, this could use adaptive questioning
    // based on previous responses to optimize the assessment
    return currentQuestionIndex + 1;
  };

  const processAssessmentResults = async (
    finalResponses: AssessmentResponse[]
  ) => {
    setIsProcessing(true);

    try {
      const results = calculateAssessmentResults(finalResponses);

      // Update the AI profile with assessment results
      await dispatch(
        updateSkillAssessment({
          id: `assessment_${Date.now()}`,
          userId: 'current_user', // This should come from auth state
          assessmentType: 'initial',
          skillLevel: results.skillLevel,
          experienceAreas: results.experienceAreas,
          preferredMethods: results.preferredMethods,
          availableEquipment: results.availableEquipment,
          timeAvailability: results.timeAvailability,
          learningGoals: results.learningGoals,
          confidence: results.overallConfidence,
          completedAt: results.completedAt,
          results: finalResponses.map(r => ({
            questionId: r.questionId,
            questionType: r.questionType,
            response: r.response,
            confidence: r.confidence,
            timeSpent: r.timeSpent,
          })),
        })
      );

      onComplete(results);
    } catch (error) {
      logger.error('Error processing assessment results:', error);
      // Handle error - could show error dialog or retry
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateAssessmentResults = (
    finalResponses: AssessmentResponse[]
  ): AssessmentResults => {
    const duration = (Date.now() - startTime) / 1000 / 60; // in minutes

    // Extract key information from responses
    const experienceResponse = finalResponses.find(
      r => r.questionId === 'experience_level'
    );
    const methodsResponse = finalResponses.find(
      r => r.questionId === 'brewing_methods'
    );
    const equipmentResponse = finalResponses.find(
      r => r.questionId === 'equipment_ownership'
    );
    const timeResponse = finalResponses.find(
      r => r.questionId === 'time_availability'
    );
    const goalsResponse = finalResponses.find(
      r => r.questionId === 'learning_goals'
    );

    // Determine skill level
    let skillLevel: SkillLevel = 'BEGINNER' as any;
    if (experienceResponse?.response) {
      skillLevel =
        experienceResponse.response.skillLevel ||
        experienceResponse.response.value ||
        ('BEGINNER' as any);
    }

    // Extract experience areas
    const experienceAreas: string[] = [];
    if (methodsResponse?.response) {
      const methods = Array.isArray(methodsResponse.response)
        ? methodsResponse.response
        : [methodsResponse.response];
      experienceAreas.push(...methods.map((m: any) => m.value || m));
    }

    // Extract preferred methods (same as experience areas for now)
    const preferredMethods = [...experienceAreas];

    // Extract available equipment
    const availableEquipment: string[] = [];
    if (equipmentResponse?.response) {
      availableEquipment.push(
        equipmentResponse.response.value || equipmentResponse.response
      );
    }

    // Extract time availability
    const timeAvailability = timeResponse?.response?.value || 10;

    // Extract learning goals
    const learningGoals: string[] = [];
    if (goalsResponse?.response) {
      const goals = Array.isArray(goalsResponse.response)
        ? goalsResponse.response
        : [goalsResponse.response];
      learningGoals.push(...goals.map((g: any) => g.value || g));
    }

    // Calculate overall confidence
    const validResponses = finalResponses.filter(r => r.response !== null);
    const overallConfidence =
      validResponses.length > 0
        ? validResponses.reduce((sum, r) => sum + r.confidence, 0) /
          validResponses.length
        : 0;

    return {
      responses: finalResponses,
      skillLevel,
      experienceAreas,
      preferredMethods,
      availableEquipment,
      timeAvailability,
      learningGoals,
      overallConfidence,
      completedAt: new Date().toISOString(),
      duration,
    };
  };

  const handleGoBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // Remove the last response
      setResponses(prev => prev.slice(0, -1));
    }
  };

  const handleExitAssessment = () => {
    setShowExitDialog(false);
    // Could save progress here for later resumption
    onComplete({
      responses,
      skillLevel: 'BEGINNER' as any,
      experienceAreas: [],
      preferredMethods: [],
      availableEquipment: [],
      timeAvailability: 10,
      learningGoals: [],
      overallConfidence: 0,
      completedAt: new Date().toISOString(),
      duration: (Date.now() - startTime) / 1000 / 60,
    });
  };

  if (isProcessing) {
    return (
      <View style={[styles.container, styles.processingContainer, style]}>
        <Card style={styles.processingCard} mode='outlined'>
          <Card.Content style={styles.processingContent}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
            <Text variant='headlineSmall' style={styles.processingTitle}>
              Processing Your Assessment
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.processingText,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              We're analyzing your responses to create your personalized coffee
              learning journey...
            </Text>
          </Card.Content>
        </Card>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      {/* Progress Indicator */}
      {showProgress && (
        <AssessmentProgress
          progress={progress}
          showDetails={false}
          style={styles.progressContainer}
        />
      )}

      {/* Current Question */}
      <AssessmentQuestion
        question={currentQuestion}
        onResponse={handleResponse}
        onSkip={handleSkip}
        allowSkip={allowSkip}
        showConfidenceSlider={true}
        style={styles.questionContainer}
      />

      {/* Navigation */}
      <View style={styles.navigationContainer}>
        <Button
          mode='outlined'
          onPress={handleGoBack}
          disabled={currentQuestionIndex === 0}
          style={styles.backButton}
        >
          Back
        </Button>

        <Text
          variant='bodySmall'
          style={[
            styles.questionCounter,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          Question {currentQuestionIndex + 1} of {assessmentQuestions.length}
        </Text>
      </View>

      {/* Exit Confirmation Dialog */}
      <Portal>
        <Dialog
          visible={showExitDialog}
          onDismiss={() => setShowExitDialog(false)}
        >
          <Dialog.Title>Exit Assessment?</Dialog.Title>
          <Dialog.Content>
            <Text variant='bodyMedium'>
              Are you sure you want to exit the assessment? Your progress will
              be saved, but you'll need to complete it later to get personalized
              recommendations.
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowExitDialog(false)}>
              Continue Assessment
            </Button>
            <Button onPress={handleExitAssessment}>Exit</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  progressContainer: {
    marginHorizontal: 16,
    marginTop: 8,
  },
  questionContainer: {
    flex: 1,
  },
  navigationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  backButton: {
    minWidth: 80,
  },
  questionCounter: {
    fontWeight: '500',
  },
  processingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  processingCard: {
    width: '100%',
    maxWidth: 400,
  },
  processingContent: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  processingTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
    fontWeight: '600',
  },
  processingText: {
    textAlign: 'center',
    lineHeight: 20,
  },
});
